/**
 * audioFx.js — reusable Web Audio DSP building blocks.
 *
 * Shared by useSynthAmbient and useStockAmbient so every ambient sound flows
 * through the same two-stage polish:
 *
 *   dry signal → convolution reverb (stereo) → soft saturation → destination
 *
 * Everything is generated procedurally. No external impulse-response files,
 * no WAV samples, no licensing questions. ~1ms init, ~0 extra memory per
 * voice after the first build because the IR + saturation curve are cached.
 *
 * v2 (iter 25.1): removed the Haas stereo widener — it was biasing the
 * image toward the left ear for mono sources (delayed-right channel causes
 * brain to localize toward the undelayed side). The convolution reverb's
 * stereo IR already supplies enough width on its own, without the imbalance.
 */

// ─── Noise buffer generators ─────────────────────────────────

/** White noise — flat spectrum, harsh. Rarely used directly. */
export function buildWhiteNoise(ctx, seconds = 2) {
  const buf = ctx.createBuffer(2, ctx.sampleRate * seconds, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  return buf;
}

/** Pink noise — -3dB/octave. Warmer than white, great for sleep/focus masking. */
export function buildPinkNoise(ctx, seconds = 2) {
  const buf = ctx.createBuffer(2, ctx.sampleRate * seconds, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < d.length; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.96900 * b2 + w * 0.1538520;
      b3 = 0.86650 * b3 + w * 0.3104856;
      b4 = 0.55000 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.0168980;
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    }
  }
  return buf;
}

/** Brown (red) noise — -6dB/octave. Deep rumble, excellent for masking traffic/snoring. */
export function buildBrownNoise(ctx, seconds = 2) {
  const buf = ctx.createBuffer(2, ctx.sampleRate * seconds, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.5; // gain-up so perceived loudness matches white noise
    }
  }
  return buf;
}

// ─── FX bus builders ─────────────────────────────────────────

let _cachedIR = null;
/**
 * Synthesize a lush hall reverb impulse response. ~4 sec decay, stereo,
 * frequency-weighted so the tail darkens naturally (high freqs die first).
 */
function buildHallIR(ctx) {
  if (_cachedIR && _cachedIR.sampleRate === ctx.sampleRate) return _cachedIR;
  const seconds = 4;
  const ir = ctx.createBuffer(2, ctx.sampleRate * seconds, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = ir.getChannelData(ch);
    for (let i = 0; i < d.length; i++) {
      const t = i / ctx.sampleRate;
      // Exponential decay + slight randomness for diffusion
      const env = Math.pow(1 - t / seconds, 2.5);
      d[i] = (Math.random() * 2 - 1) * env * 0.5;
    }
  }
  _cachedIR = ir;
  return ir;
}

let _cachedCurve = null;
/** Soft saturation curve — gentle analog-style warmth, -infinity clip avoidance. */
function buildSaturationCurve(amount = 0.4) {
  if (_cachedCurve) return _cachedCurve;
  const samples = 4096;
  const curve = new Float32Array(samples);
  const k = amount * 10;
  for (let i = 0; i < samples; i++) {
    const x = (i / samples) * 2 - 1;
    curve[i] = ((1 + k) * x) / (1 + k * Math.abs(x));
  }
  _cachedCurve = curve;
  return curve;
}

/**
 * Build the full FX chain (reverb → saturation) and return its input node +
 * output node. Caller connects their dry signal to `input` and hooks `output`
 * to `ctx.destination`.
 *
 * @param {Object}  opts
 * @param {number}  opts.wet     0..1 — reverb mix (default 0.25 = subtle)
 * @param {number}  opts.warmth  0..1 — saturation amount (default 0.3)
 */
export function buildFxBus(ctx, { wet = 0.25, warmth = 0.3 } = {}) {
  // Entry point everyone connects to
  const input = ctx.createGain();

  // Dry/wet reverb split
  const dryGain = ctx.createGain();
  dryGain.gain.value = 1 - wet * 0.5; // keep most of the dry signal
  const wetGain = ctx.createGain();
  wetGain.gain.value = wet;
  const convolver = ctx.createConvolver();
  convolver.buffer = buildHallIR(ctx);

  input.connect(dryGain);
  input.connect(convolver);
  convolver.connect(wetGain);

  // Saturation / warmth stage — gently tames peaks & adds analog color
  const shaper = ctx.createWaveShaper();
  shaper.curve = buildSaturationCurve(warmth);
  shaper.oversample = '2x';

  const mix = ctx.createGain();
  mix.gain.value = 1;
  dryGain.connect(shaper);
  wetGain.connect(shaper);
  shaper.connect(mix);

  return { input, output: mix };
}
